import { PrescriptionPrintHistoryClient } from "@/Common/Api/Patient/PrescriptionPrintHistoryClient";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Guid } from "@/Common/Models/Guid";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { LongTermMedicationIcon } from "@/Patient/Components/Medication/LongTermMedicationIcon";
import { MedicalNoteHelper } from "@/Patient/Helpers/MedicalNoteHelper";
import { PrescriptionExportableFileDispatcher } from "@/Patient/Helpers/PrescriptionExportableFileDispatcher";
import { SignedAuthorizationDocumentType } from "@/Patient/Models/MedicalNote/Enum/SignedAuthorizationDocumentType";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PatientPrescriptionItem } from "@/Patient/Models/Medication/PatientPrescriptionItem";
import { PrescriptionPrintHistory } from "@/Patient/Models/Medication/PrescriptionPrintHistory";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { PlanActionLinks } from "../PlanActionLinks";

import PrescriptionSectionStyle from "./PrescriptionSection.module.css";

import img_icon_delete from "@/Globals/_images/icons/delete.png";

interface PrescriptionSectionProps {
    encounter: Encounter;
    prescriptions: Immutable.List<PatientPrescriptionItem>;
    isReadOnly: boolean;
    isEditMode: boolean;
    isSignedOff: boolean;
    patientId: Guid;
    preferredPharmacy: IPharmacyInfo;
    noteModel: IPatientMedicalNoteModel;
    getRevokeSignaturePopupModel: (id: string, type: SignedAuthorizationDocumentType) => void;
}

function PrescriptionSection(props: PrescriptionSectionProps): JSX.Element {
    const { getText } = useTranslation();
    const sentFaxes = props.noteModel?.get("SentFaxes") ?? {};

    const openPrescription = (prescription: PatientPrescriptionItem/* null for new prescription */): void => {
        sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, {
            value: PatientTab.Prescription,
            encounterId: props.encounter.Id,
            isPrescriptionTabActive: true,
            prescriptionId: prescription?.PrescriptionId
        });
    };

    const onActiveMedicationClick = (): void => {
        const changeTab = new LocalEvent(LocalPatientOperations.UI.ChangeVerticalTabs);
        changeTab.AddItem("value", PatientTab.Medication);
        LocalEventDispatcher.dispatch(changeTab);
    };

    const faxOrPrintPrescription = (prescription: PatientPrescriptionItem, exportMode: ExportMode = ExportMode.Print): void => {
        const patient = patientStore.getCurrentPatient();

        if (patient?.IndividualResponse?.Contact_id === props.patientId) {
            const addresses = patient?.Addresses ?? [];
            const preferredAddress = addresses.firstPreferredOrDefault(p => p.IsPreferred);

            if (addresses.length > 0 && (!preferredAddress.CountryLookupId || !preferredAddress.StateLookupId)) {
                displayToast(MessageToast.Error, getText("PatientMissingCountryOrStateLabel"));
                return;
            }
        }

        if (cacheStore.ClinicSettings.EnableMedicationsPrintFilterAndHistory
            && prescription.SignedPrintAuthorizations
            && exportMode === ExportMode.Print) {
            PrescriptionPrintHistoryClient.addPrescriptionPrintHistory(prescription.PrescriptionId)
                .then(addPrescriptionPrintHistoryResponse => {
                    const prescriptionPrintHistory = new PrescriptionPrintHistory(addPrescriptionPrintHistoryResponse.data);

                    patientStore.addPrescriptionPrintHistory(prescriptionPrintHistory, patientStore.getCurrentPatientContactId());
                });
        }

        PrescriptionExportableFileDispatcher.openSignFaxPrintPopupForPrescription({
            patientContactId: props.patientId,
            prescription,
            exportMode,
            preferredPharmacy: props.preferredPharmacy,
            onSignButtonCallback: () => patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                { prescriptonIds: MedicalNoteHelper.getPrescriptiondIds(props.prescriptions, props.encounter.Id) }
            )
        });
    };

    const openPendingTransaction = (prescription: PatientPrescriptionItem, isSignedOff: boolean): void => {
        if (!isSignedOff) {
            openPrescription(prescription);
        }
    };

    const prescriptionGroups = props.prescriptions.
        toArray().filter(prescription => prescription.EncounterId == props.encounter.Id);

    const renderingElements: JSX.Element[] = [];

    const canQuickSignDocuments = cacheStore.ClinicSettings.EnableQuickDocumentSigning && !props.isReadOnly;
    const prescriptionItemContainer = canQuickSignDocuments ? PrescriptionSectionStyle.prescriptionItemContainerFormatted : PrescriptionSectionStyle.prescriptionItemContainer;

    if (isArrayNullOrUndefinedOrEmpty(prescriptionGroups)) {
        if (props.encounter.IsSignedOff) {
            return null;
        }
        else {
            renderingElements.push(
                <tr key="prescription-sections-prescription-groups">
                    <td className="c1 bottom-pad">
                        <strong>{getText("PrescriptionLabel")}</strong>
                    </td>
                    <td className={`c2 ${canQuickSignDocuments ? "" : "bottom-pad"}`}>
                        <div className="tb-row tb">
                            <div className="add-btn-column">
                                {props.isEditMode && (
                                    <a
                                        onClick={() => openPrescription(null)}
                                        data-cy="medicalNote-addPrescription"
                                    >
                                        [+]
                                    </a>
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            );
        }
    }

    prescriptionGroups.forEach((prescriptionGroup: PatientPrescriptionItem, index: number) => {
        const drugsRender: JSX.Element[] = [];

        prescriptionGroup.Medications.forEach((prescriptionMedication: MedicationModel) => {
            let expiredClassName: string = "";
            let ShowDosage: boolean = true;
            const dosageRender: JSX.Element[] = [];
            const reason = prescriptionMedication.getReasonWithLabel();
            const instruction = prescriptionMedication.GetInstruction();
            const firstDispensationString = prescriptionMedication.getFirstDispensationString();
            const dosages = prescriptionMedication.GetDosagesDisplay(
                {
                    includeRefillQuantity: true,
                    includeRenewalsOnLastDosage: true,
                    includeDaysOrQty: true,
                    includeRemainingRenewalCalculation: false
                }
            );

            const isCeasedInThisPrescription = prescriptionMedication.isCeasedMedInSpecificPrescription(
                prescriptionGroup.PrescriptionId,
                prescriptionGroup.PrescriptionDate
            );

            if (prescriptionMedication.Model.SummaryDisplayStatus == 1) { // Expiré
                expiredClassName = "red-text";
            }
            else if (isCeasedInThisPrescription) { // Ceased
                expiredClassName = "red-text";
                ShowDosage = false;
            }

            dosages.forEach((dosage: string, index: number) => dosageRender.push(
                <div key={"_dosage_" + dosage + "_" + index + "_in-prescription"} className={expiredClassName}>
                    {dosage}
                </div>
            ));

            if (firstDispensationString) {
                dosageRender.push(
                    <div key="_dosage_firstdispensation" className={expiredClassName}>
                        {firstDispensationString}
                    </div>
                );
            }

            const isLongTerm = cacheStore.ClinicSettings.EnableLongTermMedication && prescriptionMedication.Model.IsLongTerm;

            drugsRender.push(
                <div
                    key={"activeMeds_" + prescriptionMedication.Model.Id}
                    className={`note-items-list-block ${expiredClassName ?? ""} ${canQuickSignDocuments ? PrescriptionSectionStyle.prescriptionTitle : ""}`}
                >
                    <div className={`flex-container ${PrescriptionSectionStyle.activeMedicationContainerFormatted}`}>
                        <a onClick={() => onActiveMedicationClick()}>
                            <div className="flex-container flex-start">
                                <div className={PrescriptionSectionStyle.activeMedicationItem}>
                                    <div>
                                        <span className={`flex-container flex-col-gap-xs ${expiredClassName}`}>
                                            {isLongTerm && <LongTermMedicationIcon />}
                                            {prescriptionMedication.GetNameWithStatus(prescriptionGroup.PrescriptionId, prescriptionGroup.PrescriptionDate)}
                                        </span>
                                    </div>
                                    {((prescriptionMedication.isCeasedMedInSpecificPrescription(
                                        prescriptionGroup.PrescriptionId,
                                        prescriptionGroup.PrescriptionDate
                                    )) && !IsNullOrUndefined(reason)) && (
                                        <span className={PrescriptionSectionStyle.activeMedicationItemReason}>
                                            {prescriptionMedication.getReasonWithLabel()}
                                        </span>
                                    )
                                    }
                                    {((prescriptionMedication.isCeasedMedInSpecificPrescription(
                                        prescriptionGroup.PrescriptionId,
                                        prescriptionGroup.PrescriptionDate
                                    )) && !IsNullOrUndefined(reason)) && <div />
                                    }
                                    {((!prescriptionMedication.isCeasedMedInSpecificPrescription(
                                        prescriptionGroup.PrescriptionId,
                                        prescriptionGroup.PrescriptionDate
                                    )) && instruction.length != 0) && (
                                        <span className={PrescriptionSectionStyle.activeMedicationItemInstruction}>
                                            {JSXHelper.BreakStringIntoParagraphs(prescriptionMedication.GetInstruction())}
                                        </span>
                                    )
                                    }

                                </div>
                                {ShowDosage && (
                                    <div className={`${PrescriptionSectionStyle.dosage} margin-xs-left ${expiredClassName}`}>
                                        <span>
                                            {dosageRender}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {prescriptionMedication.PrescriptionTab_OriginalMedication && !isCeasedInThisPrescription && (
                                <>
                                    <div className={`flex-container ${PrescriptionSectionStyle.activeMedicationOriginalMedicationContainer ?? ""}`}>
                                        {prescriptionMedication.PrescriptionTab_OriginalMedication.Model.PrescriptionStatusInt == 2 && (
                                            <div
                                                className={`${PrescriptionSectionStyle.activeMedicationOriginalMedicationBackground ?? ""} ${PrescriptionSectionStyle.activeMedicationArrowUp ?? ""}`}
                                            />
                                        )}
                                        {prescriptionMedication.PrescriptionTab_OriginalMedication.Model.PrescriptionStatusInt == 3 && (
                                            <div
                                                className={`${PrescriptionSectionStyle.activeMedicationOriginalMedicationBackground ?? ""} ${PrescriptionSectionStyle.activeMedicationArrowDown ?? ""}`}
                                            />
                                        )}
                                        <div>
                                            <span className="red-text">
                                                {prescriptionMedication.GetNameForChildMedication(false)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="red-text margin-md-left">
                                                {prescriptionMedication.
                                                    PrescriptionTab_OriginalMedication.
                                                    GetDosageString({ includeDaysOrQty: false, includeRefillQuantity: false })}
                                            </span>
                                        </div>
                                    </div>
                                    {!IsNullOrUndefined(prescriptionMedication.PrescriptionTab_OriginalMedication.Model.CeaseReason) &&
                                    (
                                        <div className={`flex-container ${PrescriptionSectionStyle.activeMedicationOriginalMedicationContainer ?? ""}`}>
                                            <span className={`padding-md-left ${PrescriptionSectionStyle.activeMedicationItemReason ?? ""}`}>
                                                {`${getText("ReasonLabel")}: ${prescriptionMedication.PrescriptionTab_OriginalMedication.Model.CeaseReason ?? ""}`}
                                            </span>
                                        </div>
                                    )
                                    }
                                </>
                            )}
                        </a>
                    </div>
                </div>
            );
        });

        renderingElements.push(
            <tr key={prescriptionGroup.PrescriptionId}>
                <td className="c1 bottom-pad">
                    <div className={canQuickSignDocuments ? "margin-md-top" : ""}>
                        {index == 0 && <strong>{getText("PrescriptionLabel")}</strong>}
                    </div>
                </td>
                <td className={`c2 ${canQuickSignDocuments ? "" : "bottom-pad"}`}>
                    <div className="tb-row tb">
                        <div className={`add-btn-column ${canQuickSignDocuments ? "padding-md-top" : ""}`}>
                            {!props.isSignedOff && !props.isReadOnly && index == 0 && <a onClick={() => openPrescription(null)} data-cy="medicalNote-add-prescription">[+]</a>}
                        </div>
                        <div className={`${prescriptionItemContainer} ${canQuickSignDocuments && index === 0 ? PrescriptionSectionStyle.firstPrescriptionItem : ""}`}>
                            {prescriptionGroup.GeneralInstruction?.trim() && !canQuickSignDocuments && (
                                <div>
                                    <b>{getText("GeneralInstructionsLabel")}: </b>
                                    <span>{prescriptionGroup.GeneralInstruction}</span>
                                </div>
                            )}
                            {!prescriptionGroup.isPending && (
                                <div className={PrescriptionSectionStyle.prescriptionContainerFormatted}>
                                    <div className={PrescriptionSectionStyle.prescriptionMedicationContainerFormatted}>
                                        {prescriptionGroup.GeneralInstruction?.trim() && canQuickSignDocuments && (
                                            <div className={PrescriptionSectionStyle.generalInstructionContainer} data-cy="medicalNote-prescriptionGeneralInstructions">
                                                <div>
                                                    <strong>{getText("GeneralInstructionsLabel")}:</strong>
                                                    {prescriptionGroup.GeneralInstruction}
                                                </div>
                                            </div>
                                        )}
                                        <div className={PrescriptionSectionStyle.prescriptionTitleContainer}>
                                            {!canQuickSignDocuments && (
                                                <div className={PrescriptionSectionStyle.prescriptionDot}>•</div>
                                            )}
                                            <div onClick={null} className="pull-left" data-cy="medicalNote-prescriptionDrugs">
                                                {drugsRender}
                                            </div>
                                        </div>
                                    </div>
                                    {typeof prescriptionGroup.PrescriptionId === "string" && !props.isReadOnly && !cacheStore.ClinicSettings.EnableQuickDocumentSigning && (
                                        <PlanActionLinks
                                            formId={prescriptionGroup.PrescriptionId}
                                            sentFaxInfo={sentFaxes[prescriptionGroup.PrescriptionId]}
                                            canSignElement={sessionStore.canSignPrescription(prescriptionGroup.PrecribedByEmployeeId)}
                                            onActionClick={mode => faxOrPrintPrescription(prescriptionGroup, mode)}
                                            onRevokeSignature={() => props.getRevokeSignaturePopupModel(
                                                prescriptionGroup.PrescriptionId,
                                                SignedAuthorizationDocumentType.Prescription
                                            )}
                                            signedPrintAuthorizations={prescriptionGroup.SignedPrintAuthorizations}
                                        />
                                    )}

                                </div>
                            )}
                            {!prescriptionGroup.isPending && typeof prescriptionGroup.PrescriptionId === "string" && !props.isReadOnly && cacheStore.ClinicSettings.EnableQuickDocumentSigning && (
                                <PlanActionLinks
                                    formId={prescriptionGroup.PrescriptionId}
                                    sentFaxInfo={sentFaxes[prescriptionGroup.PrescriptionId]}
                                    canSignElement={sessionStore.canSignPrescription(prescriptionGroup.PrecribedByEmployeeId)}
                                    onActionClick={mode => faxOrPrintPrescription(prescriptionGroup, mode)}
                                    onRevokeSignature={() => props.getRevokeSignaturePopupModel(
                                        prescriptionGroup.PrescriptionId,
                                        SignedAuthorizationDocumentType.Prescription
                                    )}
                                    signedPrintAuthorizations={prescriptionGroup.SignedPrintAuthorizations}
                                />
                            )}
                        </div>
                        {prescriptionGroup.isPending && (
                            <div className={PrescriptionSectionStyle.prescriptionContainerFormatted}>
                                <div className={`text-nowrap ${PrescriptionSectionStyle.prescriptionMedicationContainerFormatted ?? ""}`}>
                                    {prescriptionGroup.GeneralInstruction?.trim() && canQuickSignDocuments && (
                                        <div className={PrescriptionSectionStyle.generalInstructionContainer}>
                                            <span>
                                                <strong>{getText("GeneralInstructionsLabel")}:</strong>
                                                {prescriptionGroup.GeneralInstruction}
                                            </span>
                                        </div>
                                    )}
                                    {!canQuickSignDocuments && (
                                        <div className={PrescriptionSectionStyle.prescriptionDot}>•</div>
                                    )}
                                    <div
                                        className={PrescriptionSectionStyle.prescriptionDSQPendingLabelContainer}
                                        onClick={() => openPendingTransaction(prescriptionGroup, props.isSignedOff)}
                                    >
                                        <a className={PrescriptionSectionStyle.prescriptionDSQPendingLabel}>
                                            {`${getText("DSQPendingLabel")}...(${prescriptionGroup.Medications.length} ${getText("ItemsDLabel").toLocaleLowerCase()})`}
                                        </a>
                                    </div>
                                    <div
                                        className={`pointer ${PrescriptionSectionStyle.prescriptionChangeMedicationContainer ?? ""}`}
                                        onClick={() => patientStore.ActionsMedicalNote.ExecuteDSQ(
                                            ServerOperations.Server.Myle_Patient_DeletePendingPrescription,
                                            { PrescriptionId: prescriptionGroup.PrescriptionId, skipDSQ: false }
                                        )}
                                    >
                                        <img
                                            className={`file-img ${PrescriptionSectionStyle.prescriptionChangeMedicationImage ?? ""}`}
                                            alt="change medication"
                                            src={img_icon_delete}
                                            aria-expanded="false"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div style={{ clear: "both", display: "none" }} />
                </td>
            </tr>
        );
    });

    return (
        <>
            {renderingElements}
        </>
    );
}

export { PrescriptionSection };
